import get from 'lodash/get';
import {useSelector} from 'react-redux';

import useHydrated from './useHydrated';
import {RootState} from '../store/types';
import {UIState} from '../store/data/ui/types';

/**
 * `prop` of the UI state. Runs for every `useUI` subscriber at every store update: a plain read, not a new
 * memoized selector per call (creating one cost more than reading, and its cache was never reused), nor
 * `selectUI` (an identity selector: its memoization only adds a cache lookup).
 *
 * @param {RootState} state - Redux state
 * @param {string} prop - Path in the UI state
 * @returns {unknown} - The value
 */
export const uiSelectorCreator = (state: RootState, prop: string) => get(state.ui, prop);

/**
 * Hook for handling the UI state
 *
 * `isServer` stays `true` while the component hydrates: the first page flips it to `false` in Redux, and a
 * Suspense boundary hydrated after that (React defers them, and a memoized parent no longer forces them)
 * would otherwise render client-only content over the server HTML (hydration mismatch).
 */
const useUI = <T extends keyof UIState>(prop: T): UIState[typeof prop] => {
	const value = useSelector((state: RootState) => uiSelectorCreator(state, prop));
	const isHydrated = useHydrated();

	return (prop === 'isServer' && !isHydrated ? true : value) as UIState[typeof prop];
};

export default useUI;
